import { TableCell, TableRow } from '@heroui/react';
import React, { useCallback } from 'react';
import { FiMessageCircle } from 'react-icons/fi';

import { ChocoCheckbox } from '../../../../../../components/FormComponents';
import {
  LoadingTable,
  LoadingTableColumn,
} from '../../../../../../components/Table';
import { BuyerChat } from '../../types';

interface BuyerChatsTableProps {
  chats: BuyerChat[];
  loading: boolean;
  userId: string;
  selectedChats: Set<string>;
  onSelectChats: (
    chats: Set<string> | ((prev: Set<string>) => Set<string>)
  ) => void;
  onScroll: (e: React.UIEvent<HTMLDivElement>) => void;
}

export const BuyerChatsTable: React.FC<BuyerChatsTableProps> = ({
  chats,
  loading,
  userId,
  selectedChats,
  onSelectChats,
  onScroll,
}) => {
  const allSelected =
    chats.length > 0 && chats.every((chat) => selectedChats.has(chat.id));
  const someSelected =
    !allSelected && chats.some((chat) => selectedChats.has(chat.id));

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked && !allSelected) {
        // Select all chats
        onSelectChats(new Set(chats.map((chat) => chat.id)));
      } else {
        // Deselect all chats
        onSelectChats(new Set());
      }
    },
    [chats, onSelectChats, allSelected]
  );

  const handleSelectChat = useCallback(
    (chatId: string, checked: boolean) => {
      onSelectChats((prev) => {
        const next = new Set(prev);
        if (checked) {
          next.add(chatId);
        } else {
          next.delete(chatId);
        }
        return next;
      });
    },
    [onSelectChats]
  );

  const columns: LoadingTableColumn[] = [
    {
      label: '',
      className: 'w-12',
      // Custom header render to show select all checkbox
      headerRender: () => (
        <ChocoCheckbox
          isSelected={allSelected}
          isIndeterminate={someSelected}
          onValueChange={handleSelectAll}
        />
      ),
    },
    { label: 'Chat Name' },
    { label: 'Supplier Name' },
  ];

  const renderRow = (chat: BuyerChat) => (
    <TableRow key={chat.id}>
      <TableCell>
        <ChocoCheckbox
          isSelected={selectedChats.has(chat.id)}
          onValueChange={(checked) => handleSelectChat(chat.id, checked)}
        />
      </TableCell>
      <TableCell>{chat.name}</TableCell>
      <TableCell>{chat.supplierName}</TableCell>
    </TableRow>
  );

  return (
    <div onScroll={onScroll}>
      <LoadingTable
        columns={columns}
        data={chats}
        renderRow={renderRow}
        isLoading={loading}
        isEmpty={chats.length === 0}
        rowCount={3}
        ariaLabel="Buyer chats"
        emptyState={{
          title: 'No chats found',
          description: 'No chats are available for this buyer.',
          icon: FiMessageCircle,
        }}
        className="min-h-[200px]"
      />
    </div>
  );
};
